import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CreditCard, Landmark, Wallet, CheckCircle2, Loader2, ChevronLeft, ChevronRight, Settings as SettingsIcon,
  Eye, EyeOff, ShieldCheck, Webhook,
} from "lucide-react";
import { useAuthStore } from "../store/useAuthStore";
import { usePaymentSettingsStore, type PaymentSettings } from "../store/usePaymentSettingsStore";
import Button from "../components/ui/Button";
// Centralized settings submodules (moved under Settings).
import AdminTargetBonusPage from "./AdminTargetBonusPage";
import AdminCostManagementPage from "./AdminCostManagementPage";
import AdminProjectTypesPage from "./AdminProjectTypesPage";
import AdminActivityCategoriesPage from "./AdminActivityCategoriesPage";
import ProposalViewBuilderTab from "../components/settings/ProposalViewBuilderTab";
import EnvironmentConfigTab from "../components/settings/EnvironmentConfigTab";

const TABS = [
  "Payments", "Environment Configuration", "Audit Log", "Target & Bonus", "Cost Management", "Project Types", "Project Activity", "Proposal View Builder",
];
const ALLOWED = ["Admin", "Sales Director"];

const CURRENCIES = [
  { value: "USD", label: "USD - United States Dollar" },
  { value: "EUR", label: "EUR - Euro" },
  { value: "GBP", label: "GBP - British Pound" },
  { value: "PKR", label: "PKR - Pakistani Rupee" },
];
const FORMATS = [
  { value: "$1,234.56", label: "$1,234.56" },
  { value: "$1.234,56", label: "$1.234,56" },
  { value: "1,234.56 $", label: "1,234.56 $" },
];

const inputCls =
  "w-full px-4 py-2.5 rounded-lg bg-background border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary outline-none text-sm";
const cardCls = "bg-white p-6 rounded-xl border border-outline-variant shadow-sm";
const labelCls = "block text-[12px] font-medium text-on-surface-variant mb-1.5";

export default function SettingsPage() {
  const { authUser } = useAuthStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Payments");

  // Belt-and-suspenders role guard (the route is also guarded).
  useEffect(() => {
    if (authUser && !ALLOWED.includes(authUser.role)) navigate("/", { replace: true });
  }, [authUser, navigate]);

  return (
    <div className="px-5 sm:px-8 pb-10 max-w-6xl mx-auto pt-24">
      {/* Heading lives in the top nav bar (Settings) — no duplicate in-page title. */}

      {/* Horizontal tabs (sticky below the fixed top bar so they stay reachable) */}
      <div className="flex gap-1 border-b border-outline-variant mb-6 overflow-x-auto cre-scroll bg-background sticky top-16 z-10 pt-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
              tab === t ? "text-primary border-primary" : "text-on-surface-variant border-transparent hover:text-on-surface"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Payments" ? <PaymentsTab />
        : tab === "Environment Configuration" ? <EnvironmentConfigTab />
        : tab === "Audit Log" ? <AuditLogTab />
        : tab === "Target & Bonus" ? <AdminTargetBonusPage />
        : tab === "Cost Management" ? <AdminCostManagementPage />
        : tab === "Project Types" ? <AdminProjectTypesPage />
        : tab === "Project Activity" ? <AdminActivityCategoriesPage />
        : tab === "Proposal View Builder" ? <ProposalViewBuilderTab />
        : <PlaceholderTab name={tab} />}
    </div>
  );
}

function PlaceholderTab({ name }: { name: string }) {
  return (
    <div className={`${cardCls} text-center py-16`}>
      <div className="w-12 h-12 rounded-xl bg-surface-container grid place-items-center mx-auto mb-3">
        <SettingsIcon className="text-on-surface-variant" size={22} />
      </div>
      <h3 className="text-lg font-bold text-on-surface">{name}</h3>
      <p className="text-sm text-on-surface-variant mt-1 max-w-md mx-auto">
        The {name} configuration panel is part of the Settings framework and will be implemented in a future phase.
      </p>
    </div>
  );
}

/* ============================= PAYMENTS TAB ============================= */

function PaymentsTab() {
  const { settings, isLoading, isSaving, isTesting, fetchSettings, updateSettings, testConnection } = usePaymentSettingsStore();
  const [draft, setDraft] = useState<PaymentSettings | null>(null);
  const [provider, setProvider] = useState("Stripe");
  const [liveConfirm, setLiveConfirm] = useState(false);
  const [liveText, setLiveText] = useState("");
  // Show/hide for secret inputs — only ever reveals what's typed in THIS session;
  // saved secrets come back masked from the API and can only be replaced.
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const toggleReveal = (k: string) => setReveal((r) => ({ ...r, [k]: !r[k] }));

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => {
    if (settings) {
      setDraft(JSON.parse(JSON.stringify(settings)));
      setProvider(settings.activeProvider || "Stripe");
    }
  }, [settings]);

  if (isLoading || !draft) {
    return <div className="py-20 grid place-items-center text-on-surface-variant"><Loader2 className="animate-spin text-primary" size={28} /></div>;
  }

  const env = draft.activeEnvironment;
  const gateway = draft.gateways.find((g) => g.provider === provider) || draft.gateways[0];
  const cred = gateway && gateway[env];

  const setCred = (field: string, value: string) => {
    setDraft((d) => {
      if (!d) return d;
      const copy = JSON.parse(JSON.stringify(d)) as PaymentSettings;
      const gw = copy.gateways.find((g) => g.provider === provider)!;
      (gw[env] as any)[field] = value;
      return copy;
    });
  };
  const setField = <K extends keyof PaymentSettings>(key: K, value: PaymentSettings[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const toggleMethod = (key: string) =>
    setDraft((d) => d ? { ...d, methods: d.methods.map((m) => m.key === key ? { ...m, enabled: !m.enabled } : m) } : d);

  const setBank = (field: string, value: string) =>
    setDraft((d) => d ? { ...d, bankDetails: { ...(d.bankDetails || {}), [field]: value } } : d);

  const updateNotif = (event: string, channel: "email" | "inApp" | "activityCenter", val: boolean) =>
    setDraft((d) => d ? { ...d, notifications: d.notifications.map((n) => n.event === event ? { ...n, [channel]: val } : n) } : d);

  const save = () => draft && updateSettings({ ...draft, activeProvider: provider === "Bank Transfer" ? draft.activeProvider : provider });

  const num = (k: keyof PaymentSettings, v: string) => setField(k, (Number(v) || 0) as any);

  return (
    <div className="space-y-6">
      {/* Live-switch confirmation */}
      {liveConfirm && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => setLiveConfirm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-on-surface">Switch to Live Payment Environment?</h3>
            <p className="text-sm text-on-surface-variant mt-2">You are about to switch CRE to the <b>Live</b> environment. Payments created after this may result in <b>real financial transactions</b>.</p>
            <label className="block text-xs font-semibold text-on-surface-variant mt-4 mb-1">Type <b>LIVE</b> to confirm</label>
            <input value={liveText} onChange={(e) => setLiveText(e.target.value)} className={inputCls} placeholder="LIVE" />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => { setLiveConfirm(false); setLiveText(""); }} className="px-4 py-2 rounded-lg border border-outline-variant text-sm font-semibold">Cancel</button>
              <button disabled={liveText !== "LIVE"} onClick={() => { setField("activeEnvironment", "live"); setLiveConfirm(false); setLiveText(""); }} className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold disabled:opacity-50">Switch to Live</button>
            </div>
          </div>
        </div>
      )}

      {/* 0. General Invoice Settings */}
      <div className={cardCls}>
        <h3 className="text-lg font-bold text-on-surface mb-1">General Invoice Settings</h3>
        <p className="text-[12px] text-on-surface-variant mb-5">Defaults applied to new invoices. Numbering is sequential — e.g. {draft.invoicePrefix || "INV-"}{draft.nextInvoiceNumber || 1001}, {draft.invoicePrefix || "INV-"}{(draft.nextInvoiceNumber || 1001) + 1}…</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
          <div><label className={labelCls}>Invoice Prefix</label><input className={inputCls} value={draft.invoicePrefix || ""} onChange={(e) => setField("invoicePrefix", e.target.value as any)} placeholder="INV-" /></div>
          <div><label className={labelCls}>Next Invoice Number</label><input type="number" className={inputCls} value={draft.nextInvoiceNumber ?? 1001} onChange={(e) => num("nextInvoiceNumber", e.target.value)} /></div>
          <div><label className={labelCls}>Default Payment Terms</label><input className={inputCls} value={draft.defaultPaymentTerms || ""} onChange={(e) => setField("defaultPaymentTerms", e.target.value as any)} placeholder="Net 15" /></div>
          <div><label className={labelCls}>Default Due Period (days)</label><input type="number" className={inputCls} value={draft.invoiceDuePeriodDays ?? 15} onChange={(e) => num("invoiceDuePeriodDays", e.target.value)} /></div>
          <div><label className={labelCls}>Invoice Expiration (days)</label><input type="number" className={inputCls} value={draft.invoiceExpirationDays ?? 30} onChange={(e) => num("invoiceExpirationDays", e.target.value)} /></div>
          <div><label className={labelCls}>Default Tax (%)</label><input type="number" className={inputCls} value={draft.defaultTaxPercent ?? 0} onChange={(e) => num("defaultTaxPercent", e.target.value)} /></div>
          <div><label className={labelCls}>Default Discount (%)</label><input type="number" className={inputCls} value={draft.defaultDiscountPercent ?? 0} onChange={(e) => num("defaultDiscountPercent", e.target.value)} /></div>
        </div>
        <div className="flex justify-end mt-4 pt-4 border-t border-outline-variant"><Button variant="primary" onClick={save} isLoading={isSaving}>Save Changes</Button></div>
      </div>

      {/* 0b. Payment Gateway Overview — one glance at what's configured/enabled/connected */}
      <div className={cardCls}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-on-surface">Payment Gateways</h3>
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${env === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{env === "live" ? "🟢 LIVE ENVIRONMENT" : "🟡 TESTING ENVIRONMENT"}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="bg-surface-container-low text-on-surface-variant text-left">
                <th className="p-3 font-semibold rounded-l-lg">Gateway</th>
                <th className="p-3 font-semibold">Testing</th>
                <th className="p-3 font-semibold">Live</th>
                <th className="p-3 font-semibold">Enabled</th>
                <th className="p-3 font-semibold">Status ({env})</th>
                <th className="p-3 font-semibold rounded-r-lg text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {draft.gateways.map((g) => {
                const key = g.provider === "PayPal" ? "paypal" : "stripe";
                const enabled = !!draft.methods.find((m) => m.key === key)?.enabled;
                const configured = (c: any) => !!(c?.secretKey && c?.webhookSecret);
                const st = (g as any)[env]?.connectionStatus;
                const pill = (ok: boolean, yes = "Configured", no = "Not configured") => (
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${ok ? "bg-emerald-100 text-emerald-700" : "bg-surface-container text-on-surface-variant"}`}>{ok ? yes : no}</span>
                );
                return (
                  <tr key={g.provider} className="border-b border-outline-variant last:border-0">
                    <td className="p-3 font-semibold text-on-surface">{g.provider}</td>
                    <td className="p-3">{pill(configured(g.sandbox))}</td>
                    <td className="p-3">{pill(configured(g.live))}</td>
                    <td className="p-3">{pill(enabled, "ON", "OFF")}</td>
                    <td className="p-3">
                      {st === "success" ? pill(true, "Connected") : st === "failed"
                        ? <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Failed</span>
                        : <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Not tested</span>}
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => setProvider(g.provider)} className="text-primary text-[12px] font-semibold hover:underline">Configure</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-on-surface-variant mt-3">A gateway can only be switched ON once its credentials are saved, the connection test passes and the webhook secret is set for the active environment.</p>
      </div>

      {/* 1. Payment Gateway Configuration */}
      <div className={cardCls}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <h3 className="text-lg font-bold text-on-surface">Payment Gateway Configuration</h3>
          <div className="flex items-center gap-2">
            <span className="text-[12px] font-medium text-on-surface-variant">Active Environment:</span>
            <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${env === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{env === "live" ? "🟢 LIVE" : "🟡 SANDBOX"}</span>
            <div className="flex items-center gap-1 bg-surface-container-low p-1 rounded-lg">
              {(["sandbox", "live"] as const).map((e) => (
                <button
                  key={e}
                  onClick={() => e === "live" ? setLiveConfirm(true) : setField("activeEnvironment", e)}
                  className={`px-3 py-1 rounded-md text-xs font-bold capitalize transition-all ${env === e ? "bg-white shadow-sm text-on-surface" : "text-on-surface-variant hover:bg-white/50"}`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Provider sub-tabs (Stripe / PayPal / Bank Transfer) */}
        <div className="flex border-b border-outline-variant mb-6 overflow-x-auto cre-scroll">
          {[...draft.gateways.map((g) => g.provider), "Bank Transfer"].map((p) => (
            <button
              key={p}
              onClick={() => setProvider(p)}
              className={`px-4 py-2 border-b-2 text-sm font-semibold whitespace-nowrap transition-colors ${provider === p ? "border-primary text-primary" : "border-transparent text-on-surface-variant hover:text-on-surface"}`}
            >
              {p}
            </button>
          ))}
        </div>

        {provider === "Bank Transfer" ? (
          <>
            <p className="text-[12px] text-on-surface-variant mb-4">Shown to the client on the invoice payment page when they choose Bank Transfer.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className={labelCls}>Account Holder Name</label><input className={inputCls} value={draft.bankDetails?.accountName || ""} onChange={(e) => setBank("accountName", e.target.value)} placeholder="e360 Digital Pro" /></div>
              <div><label className={labelCls}>Bank Name</label><input className={inputCls} value={draft.bankDetails?.bankName || ""} onChange={(e) => setBank("bankName", e.target.value)} placeholder="e.g. HBL" /></div>
              <div><label className={labelCls}>Account Number</label><input className={inputCls} value={draft.bankDetails?.accountNumber || ""} onChange={(e) => setBank("accountNumber", e.target.value)} /></div>
              <div><label className={labelCls}>IBAN</label><input className={inputCls} value={draft.bankDetails?.iban || ""} onChange={(e) => setBank("iban", e.target.value)} /></div>
              <div><label className={labelCls}>SWIFT / BIC</label><input className={inputCls} value={draft.bankDetails?.swift || ""} onChange={(e) => setBank("swift", e.target.value)} /></div>
              <div><label className={labelCls}>Routing Number</label><input className={inputCls} value={draft.bankDetails?.routingNumber || ""} onChange={(e) => setBank("routingNumber", e.target.value)} /></div>
              <div className="md:col-span-2"><label className={labelCls}>Instructions (optional)</label><textarea className={inputCls} rows={2} value={draft.bankDetails?.instructions || ""} onChange={(e) => setBank("instructions", e.target.value)} placeholder="e.g. Include the invoice number as payment reference." /></div>
            </div>
          </>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{provider === "PayPal" ? "Client ID" : "Publishable Key"}</label>
              <input type="text" className={inputCls} value={cred.publishableKey} onChange={(e) => setCred("publishableKey", e.target.value)} placeholder={provider === "PayPal" ? "PayPal Client ID" : "pk_..."} />
            </div>
            <div>
              <label className={labelCls}>{provider === "PayPal" ? "Client Secret" : "Secret Key"}</label>
              <div className="relative">
                <input type={reveal[`${provider}-${env}-secret`] ? "text" : "password"} className={`${inputCls} pr-10`} value={cred.secretKey} onChange={(e) => setCred("secretKey", e.target.value)} placeholder={provider === "PayPal" ? "PayPal Client Secret" : "sk_..."} />
                <button type="button" onClick={() => toggleReveal(`${provider}-${env}-secret`)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface" title="Show / hide">
                  {reveal[`${provider}-${env}-secret`] ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="text-[10px] text-on-surface-variant mt-1">Saved keys are stored encrypted and shown masked — type a new value to replace.</p>
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Webhook URL <span className="text-[10px] text-outline">(paste this into {provider})</span></label>
              <input type="text" readOnly className={`${inputCls} bg-surface-container-low`} value={cred.webhookUrl} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>{provider === "PayPal" ? "Webhook ID" : "Webhook Signing Secret"}</label>
              <div className="relative">
                <input type={reveal[`${provider}-${env}-wh`] ? "text" : "password"} className={`${inputCls} pr-10`} value={cred.webhookSecret} onChange={(e) => setCred("webhookSecret", e.target.value)} placeholder={provider === "PayPal" ? "WH-..." : "whsec_..."} />
                <button type="button" onClick={() => toggleReveal(`${provider}-${env}-wh`)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant hover:text-on-surface" title="Show / hide">
                  {reveal[`${provider}-${env}-wh`] ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            {/* Webhook health — from the events CRE has actually received */}
            {(() => {
              const w = draft.webhookStatus?.[provider];
              const tone = w?.health === "healthy" ? "bg-emerald-100 text-emerald-700" : w?.health === "stale" ? "bg-amber-100 text-amber-700" : "bg-surface-container text-on-surface-variant";
              const label = w?.health === "healthy" ? "Healthy" : w?.health === "stale" ? "No events in 30 days" : "No events received yet";
              return (
                <div className="md:col-span-2 rounded-lg border border-outline-variant bg-surface-container-lowest p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-on-surface"><Webhook size={14} className="text-primary" /> Webhook Status</span>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${tone}`}>{label}</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2 text-[12px]">
                    <div><div className="text-[10px] uppercase tracking-wide text-on-surface-variant">Events received</div><div className="font-semibold text-on-surface">{w?.totalEvents ?? 0}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wide text-on-surface-variant">Last event</div><div className="font-semibold text-on-surface">{w?.lastEventAt ? new Date(w.lastEventAt).toLocaleString() : "—"}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wide text-on-surface-variant">Last event type</div><div className="font-semibold text-on-surface truncate" title={w?.lastEventType}>{w?.lastEventType || "—"}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wide text-on-surface-variant">Last result</div><div className="font-semibold text-on-surface">{w?.lastResult ? `${w.lastResult}${w.lastInvoice ? ` · ${w.lastInvoice}` : ""}` : "—"}</div></div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 pt-4 mt-4 border-t border-outline-variant">
          {provider === "Bank Transfer" ? <span className="text-[12px] text-on-surface-variant">Bank details are shown to clients — no connection test needed.</span> : <ConnStatus status={cred.connectionStatus} lastTestedAt={cred.lastTestedAt} />}
          <div className="flex gap-3">
            {provider !== "Bank Transfer" && (
              <button
                onClick={() => testConnection(provider, env)}
                disabled={isTesting}
                className="border border-outline-variant text-on-surface-variant px-4 py-2 rounded-lg text-sm font-semibold hover:bg-surface-container-low transition-all disabled:opacity-50"
              >
                {isTesting ? "Testing…" : "Test Connection"}
              </button>
            )}
            <Button variant="primary" onClick={save} isLoading={isSaving}>Save Changes</Button>
          </div>
        </div>
      </div>

      {/* 2. Supported Payment Methods */}
      <div className={cardCls}>
        <h3 className="text-lg font-bold text-on-surface mb-6">Supported Payment Methods</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {draft.methods.map((m) => {
            const Icon = m.key === "bank_transfer" ? Landmark : m.key === "paypal" ? Wallet : CreditCard;
            return (
              <div key={m.key} className={`p-4 rounded-lg border border-outline-variant bg-surface-container-lowest ${!m.enabled ? "opacity-70" : ""}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon size={18} className={m.enabled ? "text-primary" : "text-outline"} />
                    <span className="text-sm font-semibold text-on-surface">{m.label}</span>
                  </div>
                  <input type="checkbox" className="w-4 h-4 accent-primary" checked={m.enabled} onChange={() => toggleMethod(m.key)} />
                </div>
                <label className={`flex items-center gap-2 text-[13px] ${m.enabled ? "text-on-surface-variant cursor-pointer" : "text-outline cursor-not-allowed"}`}>
                  <input type="radio" name="default_method" className="accent-primary" checked={draft.defaultMethod === m.key} disabled={!m.enabled} onChange={() => setField("defaultMethod", m.key)} /> Mark as Default
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Payment Preferences */}
      <div className={cardCls}>
        <h3 className="text-lg font-bold text-on-surface mb-6">Payment Preferences</h3>
        <div className="space-y-8">
          {/* Currency */}
          <div>
            <h4 className="text-sm font-semibold text-on-surface mb-3 border-b border-outline-variant pb-2">Currency Settings</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className={labelCls}>Default Currency</label>
                <select className={inputCls} value={draft.defaultCurrency} onChange={(e) => setField("defaultCurrency", e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Format</label>
                <select className={inputCls} value={draft.currencyFormat} onChange={(e) => setField("currencyFormat", e.target.value)}>
                  {FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Invoice */}
          <div>
            <h4 className="text-sm font-semibold text-on-surface mb-3 border-b border-outline-variant pb-2">Invoice Settings</h4>
            <div className="max-w-2xl space-y-3">
              <div className="max-w-xs">
                <label className={labelCls}>Default Due Period (Days)</label>
                <input type="number" min={0} className={inputCls} value={draft.invoiceDuePeriodDays} onChange={(e) => setField("invoiceDuePeriodDays", Number(e.target.value) || 0)} />
              </div>
              <Check label="Auto-generate receipts on successful payment" checked={draft.autoGenerateReceipts} onChange={(v) => setField("autoGenerateReceipts", v)} />
              <Check label="Allow partial payments on invoices" checked={draft.allowPartialPayments} onChange={(v) => setField("allowPartialPayments", v)} />
              <Check label="Auto-mark invoices as paid when balance is zero" checked={draft.autoMarkPaidOnZeroBalance} onChange={(v) => setField("autoMarkPaidOnZeroBalance", v)} />
            </div>
          </div>

          {/* Wallet (Beta placeholder) */}
          <div>
            <h4 className="text-sm font-semibold text-on-surface mb-3 border-b border-outline-variant pb-2">Wallet Configuration (Beta)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <div>
                <label className={labelCls}>Max Wallet Balance</label>
                <input type="number" min={0} className={inputCls} value={draft.walletMaxBalance} onChange={(e) => setField("walletMaxBalance", Number(e.target.value) || 0)} />
              </div>
              <div>
                <label className={labelCls}>Daily Transaction Limit</label>
                <input type="number" min={0} className={inputCls} value={draft.walletDailyLimit} onChange={(e) => setField("walletDailyLimit", Number(e.target.value) || 0)} />
              </div>
            </div>
            <p className="text-[11px] text-on-surface-variant mt-2">Wallet functionality is a future capability — these values are stored for when it goes live.</p>
          </div>

          {/* Notifications */}
          <div>
            <h4 className="text-sm font-semibold text-on-surface mb-3 border-b border-outline-variant pb-2">Payment Notifications</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[420px]">
                <thead>
                  <tr className="bg-surface-container-low text-on-surface-variant text-left">
                    <th className="p-3 font-semibold rounded-l-lg">Event</th>
                    <th className="p-3 font-semibold text-center">Email</th>
                    <th className="p-3 font-semibold text-center">In-App</th>
                    <th className="p-3 font-semibold text-center rounded-r-lg">Activity Center</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.notifications.map((n) => (
                    <tr key={n.event} className="border-b border-outline-variant last:border-0">
                      <td className="p-3 text-on-surface">{n.event}</td>
                      {(["email", "inApp", "activityCenter"] as const).map((ch) => (
                        <td key={ch} className="p-3 text-center">
                          <input type="checkbox" className="w-4 h-4 accent-primary" checked={n[ch]} onChange={(e) => updateNotif(n.event, ch, e.target.checked)} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-outline-variant">
            <Button variant="primary" onClick={save} isLoading={isSaving}>Save Changes</Button>
          </div>
        </div>
      </div>

      {/* 4. Transaction Logs */}
      <TransactionLogs />
    </div>
  );
}

/* ============================= AUDIT LOG TAB ============================= */
// Security & Audit — every payment-configuration change, environment switch,
// connection test and gateway enable/disable, with who and when.
function AuditLogTab() {
  const { settings, isLoading, fetchSettings } = usePaymentSettingsStore();
  useEffect(() => { if (!settings) fetchSettings(); }, [settings, fetchSettings]);
  if (isLoading || !settings) {
    return <div className="py-20 grid place-items-center text-on-surface-variant"><Loader2 className="animate-spin text-primary" size={28} /></div>;
  }
  const rows = settings.audit || [];
  return (
    <div className={cardCls}>
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={18} className="text-primary" />
        <h3 className="text-lg font-bold text-on-surface">Payment Configuration Audit Log</h3>
      </div>
      <p className="text-[12px] text-on-surface-variant mb-5">Who changed what, and when — credentials, environment switches, connection tests and gateway activation. Most recent first.</p>
      {rows.length === 0 ? (
        <p className="text-sm text-on-surface-variant py-8 text-center">No configuration changes recorded yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="bg-surface-container-low text-on-surface-variant text-left">
                <th className="p-3 font-semibold rounded-l-lg whitespace-nowrap">Date &amp; Time</th>
                <th className="p-3 font-semibold">User</th>
                <th className="p-3 font-semibold rounded-r-lg">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr key={i} className="border-b border-outline-variant last:border-0">
                  <td className="p-3 text-on-surface-variant whitespace-nowrap">{new Date(a.at).toLocaleString()}</td>
                  <td className="p-3 font-semibold text-on-surface">{a.userName || "Admin"}</td>
                  <td className="p-3 text-on-surface">{a.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConnStatus({ status, lastTestedAt }: { status: string; lastTestedAt: string | null }) {
  if (status === "success") {
    const ago = lastTestedAt ? new Date(lastTestedAt).toLocaleString() : "";
    return (
      <div className="flex items-center gap-2 text-secondary">
        <CheckCircle2 size={18} />
        <span className="text-[13px] font-medium">Connection Successful{ago ? ` (Last tested: ${ago})` : ""}</span>
      </div>
    );
  }
  if (status === "failed") return <span className="text-[13px] font-medium text-error">Connection failed — check credentials.</span>;
  return <span className="text-[13px] text-on-surface-variant">Not tested yet.</span>;
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-on-surface cursor-pointer">
      <input type="checkbox" className="w-4 h-4 accent-primary" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
    </label>
  );
}

function TransactionLogs() {
  const { tx, isLoadingTx, fetchTransactions } = usePaymentSettingsStore();
  const [environment, setEnvironment] = useState("all");
  const [status, setStatus] = useState("all");
  const [date, setDate] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchTransactions({ environment, status, from: date || undefined, to: date || undefined, page });
  }, [fetchTransactions, environment, status, date, page]);

  const totalPages = Math.max(1, Math.ceil(tx.total / tx.limit));
  const from = tx.total === 0 ? 0 : (tx.page - 1) * tx.limit + 1;
  const to = Math.min(tx.total, tx.page * tx.limit);
  const statusCls = (s: string) =>
    s === "Successful" ? "bg-secondary-container text-on-secondary-container" : s === "Pending" ? "bg-surface-container-highest text-on-surface-variant" : "bg-error-container text-error";

  return (
    <div className={cardCls}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-bold text-on-surface">Transaction Logs</h3>
        <div className="flex flex-wrap gap-2">
          <select value={environment} onChange={(e) => { setPage(1); setEnvironment(e.target.value); }} className="px-3 py-1.5 rounded-lg border border-outline-variant text-sm bg-white">
            <option value="all">All Environments</option>
            <option value="sandbox">Sandbox</option>
            <option value="live">Live</option>
          </select>
          <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className="px-3 py-1.5 rounded-lg border border-outline-variant text-sm bg-white">
            <option value="all">All Statuses</option>
            <option value="Successful">Successful</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
          </select>
          <input type="date" value={date} onChange={(e) => { setPage(1); setDate(e.target.value); }} className="px-3 py-1.5 rounded-lg border border-outline-variant text-sm bg-white" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-surface-container-low text-on-surface-variant text-left text-[12px] uppercase tracking-wide">
              <th className="p-3 font-semibold">Transaction ID</th>
              <th className="p-3 font-semibold">Gateway / Env</th>
              <th className="p-3 font-semibold">Client</th>
              <th className="p-3 font-semibold">Invoice</th>
              <th className="p-3 font-semibold text-right">Amount</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3 font-semibold">Processed On</th>
            </tr>
          </thead>
          <tbody>
            {isLoadingTx ? (
              <tr><td colSpan={7} className="p-8 text-center text-on-surface-variant"><Loader2 className="animate-spin text-primary inline" size={20} /></td></tr>
            ) : tx.transactions.length === 0 ? (
              <tr><td colSpan={7} className="p-8 text-center text-on-surface-variant">No transactions yet. Payments will appear here once a gateway is live.</td></tr>
            ) : (
              tx.transactions.map((t) => (
                <tr key={t._id} className="border-b border-outline-variant last:border-0">
                  <td className="p-3 font-mono text-[12px] text-on-surface">{t.transactionId}</td>
                  <td className="p-3"><div className="text-on-surface">{t.gateway}</div><div className="text-[11px] text-on-surface-variant capitalize">{t.environment}</div></td>
                  <td className="p-3 text-on-surface">{t.clientName || "—"}</td>
                  <td className="p-3 text-primary">{t.invoiceNumber || "—"}</td>
                  <td className="p-3 text-right font-semibold text-on-surface">${(t.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="p-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${statusCls(t.status)}`}>{t.status}</span></td>
                  <td className="p-3 text-on-surface-variant">{new Date(t.processedOn).toLocaleString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between pt-4 text-sm text-on-surface-variant">
        <span>Showing {from} to {to} of {tx.total} entries</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 rounded-lg border border-outline-variant inline-flex items-center gap-1 disabled:opacity-40 hover:bg-surface-container-low"><ChevronLeft size={14} /> Prev</button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1.5 rounded-lg border border-outline-variant inline-flex items-center gap-1 disabled:opacity-40 hover:bg-surface-container-low">Next <ChevronRight size={14} /></button>
        </div>
      </div>
    </div>
  );
}
